import React from "react";
import {
  Box,
  Chip,
  Typography,
  Checkbox,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Collapse,
  Paper,
  Grid,
} from "@mui/material";
import { Icon } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

interface Allergy {
  id: string;
  name: string;
  icon: string;
  description: string;
  color: string;
}

const COMMON_ALLERGIES: Allergy[] = [
  {
    id: "dairy",
    name: "Dairy",
    icon: "breakfast_dining",
    description: "Contains milk or dairy products",
    color: "#2196f3", // blue
  },
  {
    id: "gluten",
    name: "Gluten",
    icon: "bakery_dining",
    description: "Contains wheat, barley, rye, or oats",
    color: "#ff9800", // orange
  },
  {
    id: "nuts",
    name: "Tree Nuts",
    icon: "forest",
    description: "Contains tree nuts (almonds, walnuts, etc.)",
    color: "#795548", // brown
  },
  {
    id: "peanuts",
    name: "Peanuts",
    icon: "egg_alt",
    description: "Contains peanuts or peanut derivatives",
    color: "#8d6e63", // brown variant
  },
  {
    id: "eggs",
    name: "Eggs",
    icon: "egg",
    description: "Contains eggs or egg products",
    color: "#fdd835", // yellow
  },
  {
    id: "soy",
    name: "Soy",
    icon: "grass",
    description: "Contains soy or soy products",
    color: "#4caf50", // green
  },
  {
    id: "fish",
    name: "Fish",
    icon: "set_meal",
    description: "Contains fish or fish products",
    color: "#03a9f4", // light blue
  },
  {
    id: "shellfish",
    name: "Shellfish",
    icon: "water",
    description: "Contains shellfish",
    color: "#e91e63", // pink
  },
  {
    id: "sesame",
    name: "Sesame",
    icon: "grain",
    description: "Contains sesame seeds or oil",
    color: "#9c27b0", // purple
  },
];

interface AllergiesSelectorProps {
  selectedAllergies: string[];
  onChange: (allergies: string[]) => void;
  defaultExpanded?: boolean;
}

export const AllergiesSelector: React.FC<AllergiesSelectorProps> = ({
  selectedAllergies,
  onChange,
  defaultExpanded = false,
}) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  const handleToggleAllergy = (allergyId: string) => {
    const newSelected = selectedAllergies.includes(allergyId)
      ? selectedAllergies.filter((id) => id !== allergyId)
      : [...selectedAllergies, allergyId];
    onChange(newSelected);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          <Icon color="warning" fontSize="small">
            warning_amber
          </Icon>
          <Typography variant="subtitle2" color="text.primary">
            Allergen Information
          </Typography>
          {selectedAllergies.length > 0 && (
            <Chip
              size="small"
              label={`${selectedAllergies.length} selected`}
              color="warning"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Tooltip title={isExpanded ? "Collapse allergens" : "Expand allergens"}>
          <IconButton
            size="small"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s",
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.warning.main, 0.2),
              },
            }}
          >
            <Icon fontSize="small">expand_more</Icon>
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2 }}>
        <AnimatePresence>
          {selectedAllergies.length > 0 && (
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
              }}
            >
              {selectedAllergies.map((allergyId) => {
                const allergy = COMMON_ALLERGIES.find(
                  (a) => a.id === allergyId
                );
                if (!allergy) return null;
                return (
                  <Chip
                    key={allergyId}
                    size="small"
                    label={allergy.name}
                    icon={<Icon fontSize="small">{allergy.icon}</Icon>}
                    onDelete={() => handleToggleAllergy(allergyId)}
                    sx={{
                      bgcolor: alpha(allergy.color, 0.1),
                      color: allergy.color,
                      borderColor: alpha(allergy.color, 0.3),
                      "& .MuiChip-deleteIcon": {
                        color: allergy.color,
                        "&:hover": {
                          color: theme.palette.error.main,
                        },
                      },
                      "& .MuiChip-icon": {
                        color: allergy.color,
                      },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </AnimatePresence>

        {selectedAllergies.length === 0 && !isExpanded && (
          <Typography variant="body2" color="text.secondary">
            No allergens selected. Click the expand button to add allergens.
          </Typography>
        )}
      </Box>

      <Collapse in={isExpanded}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.warning.main, 0.05)
                : alpha(theme.palette.warning.light, 0.05),
          }}
        >
          <Grid container spacing={1}>
            {COMMON_ALLERGIES.map((allergy) => (
              <Grid item xs={12} sm={6} md={4} key={allergy.id}>
                <Box
                  component={motion.div}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleToggleAllergy(allergy.id)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: selectedAllergies.includes(allergy.id)
                      ? alpha(allergy.color, 0.1)
                      : "background.paper",
                    border: 1,
                    borderColor: selectedAllergies.includes(allergy.id)
                      ? allergy.color
                      : "divider",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: alpha(allergy.color, 0.1),
                      borderColor: allergy.color,
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedAllergies.includes(allergy.id)}
                    sx={{
                      color: alpha(allergy.color, 0.5),
                      "&.Mui-checked": {
                        color: allergy.color,
                      },
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Icon sx={{ color: allergy.color }}>{allergy.icon}</Icon>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: selectedAllergies.includes(allergy.id)
                            ? allergy.color
                            : "text.primary",
                        }}
                      >
                        {allergy.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {allergy.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  );
};
